// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import type { IntegrationListItem } from '@greenlight/shared';

import { BlockingGrantsNotice } from '@/components/integrations/BlockingGrantsNotice';
import {
  blockingGrantsFromError,
  type BlockingGrants,
} from '@/components/integrations/blockingGrants';

export interface DeleteCredentialDialogProps {
  /** The integration the credential belongs to, or null when closed. */
  integration: IntegrationListItem | null;
  /** The credential slug being deleted, or null when closed. */
  credentialSlug: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Guarded soft-delete of a credential. Deleting deactivates the credential and
 * destroys its vault secret, but is refused (409) while any app or user holds a
 * pending/granted grant on it. On refusal the dialog names the blocking apps and
 * users and links to the grants view filtered to this integration so IT revokes them one at a
 * time (docs/09 § Credential deletion). The row is never hard-deleted, so
 * revoked/denied grant history keeps a resolvable target.
 */
export function DeleteCredentialDialog({
  integration,
  credentialSlug,
  open,
  onOpenChange,
}: DeleteCredentialDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const deleteCredential = api.integrations.deleteCredential.useMutation();
  const [blocking, setBlocking] = React.useState<BlockingGrants | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setBlocking(null);
      setError(null);
    }
  }, [open]);

  const grantsHref =
    integration != null
      ? `/integrations?view=grants&group=integration&integrationId=${integration.id}`
      : '/integrations?view=grants';

  async function handleDelete(): Promise<void> {
    if (!integration || !credentialSlug) return;
    setError(null);
    setBlocking(null);
    try {
      const res = await deleteCredential.mutateAsync({
        params: { id: integration.id, slug: credentialSlug },
      });
      if (res.status >= 300) throw res;
      toast.success(`Credential "${credentialSlug}" deleted.`);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onOpenChange(false);
    } catch (err) {
      const refused = blockingGrantsFromError(err, 'integration_credential.in_use');
      if (refused) {
        setBlocking(refused);
        return;
      }
      setError('Failed to delete the credential. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete credential</DialogTitle>
          <DialogDescription>
            {integration && credentialSlug ? (
              <>
                Permanently destroy the secret behind <MonoValue>{credentialSlug}</MonoValue> on{' '}
                {integration.name} and deactivate the credential. The grant history is kept, but no
                app can be granted this credential again.
              </>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        {blocking ? (
          <BlockingGrantsNotice
            subject="credential"
            blocking={blocking}
            grantsHref={grantsHref}
            integrationName={integration?.name}
            data-testid="delete-credential-blocked"
          />
        ) : (
          <p className="text-[12.5px] leading-relaxed text-muted-foreground">
            This can’t be undone. Deleting is blocked while any app or person holds a pending or
            granted grant on the credential.
          </p>
        )}

        {error && <p className="text-[11.5px] text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteCredential.isPending || blocking !== null}
            onClick={() => void handleDelete()}
          >
            {deleteCredential.isPending ? 'Deleting…' : 'Delete credential'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
